import React, { useState } from "react";
import GenericModal from "@ui-components/Modal/GenericModal";
import GenericButton from "@ui-components/Button/GenericButton";
import GenericInput from "@ui-components/Input/GenericInput";
import GenericCheckbox from "@ui-components/Checkbox/GenericCheckbox";
import SearchSelect from "@ui-components/Select/GenericSelect";
import { useAxiosAuth0 } from "@shared/config/useAxiosAuth0";
import {
  ACCESS_TYPE_OPTIONS,
  accessTypeLabel,
} from "../../CarrierAccess/CarrierAccessData";
import { declareExtenderPopCarrierAccessAPI } from "../requests/request";
import {
  EMPTY_MANUAL_LEG,
  apiErrorMessage,
  legSourceLabel,
  manualLegBody,
  type ManualLegDraft,
  type SiteLeg,
} from "../extenderPopCarrierAccessData";
import ManualLegFields from "./ManualLegFields";

interface DeclareCarrierAccessModalProps {
  isOpen: boolean;
  onClose: () => void;
  onDeclared: () => void;
  extenderPopId: number;
  tenantId: number;
  siteName: string | null;
  cpes: { id: number; serviceId: string; name: string | null }[];
  freeLegs: SiteLeg[];
}

/**
 * Declare the Carrier Access (84-) of the site (CTX-830): its name and type,
 * the free legs it claims — all of them unless unticked — and, when no
 * description names the carrier, a leg declared by hand.
 */
export default function DeclareCarrierAccessModal({
  isOpen,
  onClose,
  onDeclared,
  extenderPopId,
  tenantId,
  siteName,
  cpes,
  freeLegs,
}: DeclareCarrierAccessModalProps) {
  const api = useAxiosAuth0();
  const [name, setName] = useState(siteName ?? "");
  const [accessType, setAccessType] = useState("");
  const [legIds, setLegIds] = useState<number[]>(freeLegs.map((leg) => leg.id));
  const [withManualLeg, setWithManualLeg] = useState(freeLegs.length === 0);
  const [manualLeg, setManualLeg] = useState<ManualLegDraft>(EMPTY_MANUAL_LEG);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const cpeOf = new Map(cpes.map((cpe) => [cpe.id, cpe.serviceId]));
  const manualBody = withManualLeg ? manualLegBody(manualLeg) : null;
  const canSave =
    !isSaving &&
    (legIds.length > 0 || manualBody !== null) &&
    (!withManualLeg || manualBody !== null);

  const toggleLeg = (legId: number, checked: boolean) =>
    setLegIds((current) =>
      checked ? [...current, legId] : current.filter((id) => id !== legId)
    );

  const save = async () => {
    setIsSaving(true);
    setError(null);
    try {
      await declareExtenderPopCarrierAccessAPI(api, extenderPopId, tenantId, {
        ...(name.trim() ? { name: name.trim() } : {}),
        ...(accessType ? { accessType } : {}),
        legIds,
        ...(manualBody ? { manualLegs: [manualBody] } : {}),
      });
      onDeclared();
    } catch (requestError) {
      setError(apiErrorMessage(requestError, "Could not declare the Backhaul"));
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <GenericModal
      isOpen={isOpen}
      onClose={onClose}
      title="Declare Backhaul"
      size="md"
      footer={
        <div className="flex justify-end gap-2">
          <GenericButton variant="secondary" label="Cancel" onClick={onClose} />
          <GenericButton
            label="Declare"
            onClick={save}
            loading={isSaving}
            disabled={!canSave}
          />
        </div>
      }
    >
      <div className="flex flex-col gap-4">
        <GenericInput label="Name" value={name} onChange={setName} fullWidth />
        <SearchSelect
          label="Type"
          placeholder="Not set"
          options={ACCESS_TYPE_OPTIONS.map((type) => ({
            value: type,
            label: accessTypeLabel(type),
          }))}
          onSelect={setAccessType}
        />

        <div className="flex flex-col gap-2">
          <h3 className="text-sm text-semantic-text-primary">Legs</h3>
          {freeLegs.length === 0 && (
            <p className="text-sm text-semantic-text-secondary">
              No router description names a free carrier on this site.
            </p>
          )}
          {freeLegs.map((leg) => (
            <GenericCheckbox
              key={leg.id}
              checked={legIds.includes(leg.id)}
              onChange={(checked) => toggleLeg(leg.id, checked)}
              label={`${leg.name} · ${legSourceLabel(leg.source)} · ${
                cpeOf.get(leg.cpeId) ?? "-"
              }${leg.circuitId ? ` · ${leg.circuitId}` : ""}`}
            />
          ))}
          <GenericCheckbox
            checked={withManualLeg}
            onChange={setWithManualLeg}
            label="Add a leg by hand"
          />
          {withManualLeg && (
            <ManualLegFields
              extenderPopId={extenderPopId}
              tenantId={tenantId}
              cpes={cpes}
              value={manualLeg}
              onChange={setManualLeg}
            />
          )}
        </div>

        {error && <p className="text-sm text-semantic-text-error">{error}</p>}
      </div>
    </GenericModal>
  );
}
